/**
 * Paul Lin - Personal Profile Page
 */

import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, MapPin, Award, Briefcase, BookOpen, Lightbulb, ExternalLink, Mail, Linkedin } from 'lucide-react'
import { FadeIn, StaggerContainer } from '@/components/motion'
import { motion } from 'framer-motion'
import { setPageMeta } from '@/utils/seo'

const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] as const } },
}

// X (Twitter) icon
function XIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  )
}

export default function Paul() {
  useEffect(() => {
    setPageMeta({
      title: 'Paul Lin - Founder & CEO | Returned.com',
      description: 'Paul Lin is an entrepreneur with 20+ patents, Forbes Technology Council member, and founder of Returned.com. Building AI that helps humans thrive.',
      canonical: 'https://returned.com/paul-lin',
    })
  }, [])

  return (
    <div style={{ minHeight: '100vh', background: '#ffffff' }}>
      {/* Profile Header */}
      <section className="landing-section" style={{ paddingBottom: 'clamp(2rem, 4vw, 3rem)' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <FadeIn>
            {/* Back Link */}
            <Link
              to="/blog"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#6366f1',
                textDecoration: 'none',
                fontSize: '0.9rem',
                fontWeight: 500,
                marginBottom: '2rem',
                transition: 'color 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#818cf8'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#6366f1'}
            >
              <ArrowLeft size={16} />
              Back to Blog
            </Link>

            {/* Profile Card */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}>
              {/* Avatar */}
              <div style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                background: '#111111',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                fontWeight: 700,
                fontSize: '2.5rem',
                marginBottom: '1.5rem',
                boxShadow: '0 8px 32px rgba(99, 102, 241, 0.3)',
              }}>
                PL
              </div>

              <h1 style={{
                fontSize: 'clamp(2rem, 4vw, 2.5rem)',
                fontWeight: 700,
                color: '#0f0f0f',
                marginBottom: '0.5rem',
                letterSpacing: '-0.02em',
              }}>
                Paul Lin
              </h1>

              <p style={{
                fontSize: '1.1rem',
                color: '#4f46e5',
                fontWeight: 500,
                marginBottom: '1rem',
              }}>
                Founder & CEO
              </p>

              {/* Tags */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '0.75rem',
                marginBottom: '1.5rem',
              }}>
                <Tag icon={<MapPin size={12} />} text="Boulder, CO" />
                <Tag icon={<Award size={12} />} text="20+ Patents" />
                <Tag icon={<Briefcase size={12} />} text="Forbes Tech Council" />
              </div>

              {/* Social Links */}
              <div style={{
                display: 'flex',
                gap: '0.75rem',
                marginBottom: '2rem',
              }}>
                <SocialIcon href="https://x.com/paullin_me" label="X (Twitter)" icon={<XIcon size={18} />} />
                <SocialIcon href="https://www.linkedin.com/in/paullin1/" label="LinkedIn" icon={<Linkedin size={18} />} />
                <SocialIcon href="mailto:paul@returned.com" label="Email" icon={<Mail size={18} />} />
              </div>
            </div>

            {/* Bio */}
            <div style={{
              background: '#fafafa',
              border: '1px solid #eaeaea',
              borderRadius: '16px',
              padding: 'clamp(1.5rem, 3vw, 2rem)',
            }}>
              <p style={{
                color: '#3f3f3f',
                fontSize: '1.1rem',
                lineHeight: 1.8,
                marginBottom: '1rem',
              }}>
                Entrepreneur with 20+ years building technology companies. I've had exits, failures, and everything in between—including losing $6M on a startup that didn't work.
              </p>
              <p style={{
                color: '#5f5f5f',
                fontSize: '1.05rem',
                lineHeight: 1.8,
                margin: 0,
              }}>
                Now I'm focused on one question: <strong style={{ color: '#0f0f0f' }}>How do humans thrive alongside AI?</strong> I write, build tools, and run research to help people navigate this shift.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Current Work */}
      <section style={{ padding: '0 1rem clamp(2rem, 4vw, 3rem)' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <StaggerContainer stagger={0.08}>
            <motion.h2 variants={staggerItem} style={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#0f0f0f',
              marginBottom: '1.25rem',
            }}>
              Current Work
            </motion.h2>

            <motion.div variants={staggerItem} style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              marginBottom: '2.5rem',
            }}>
              <WorkCard
                icon={<Briefcase size={18} />}
                title="Returned.com"
                description="AI that makes phone calls for you"
                link="/"
              />
              <WorkCard
                icon={<Lightbulb size={18} />}
                title="The Human+AI Project"
                description="Research on human-AI coexistence"
                link="/human-ai-project"
              />
              <WorkCard
                icon={<BookOpen size={18} />}
                title="Writing"
                description="Weekly insights on AI and technology"
                link="/blog"
              />
            </motion.div>

            {/* Background */}
            <motion.h2 variants={staggerItem} style={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#0f0f0f',
              marginBottom: '1.25rem',
            }}>
              Background
            </motion.h2>

            <motion.div variants={staggerItem} style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              marginBottom: '2.5rem',
            }}>
              <BackgroundItem
                title="Forbes Technology Council"
                subtitle="Member"
              />
              <BackgroundItem
                title="Inc. 5000"
                subtitle="Top 10 Asian Entrepreneurs (2010)"
              />
              <BackgroundItem
                title="RLA Startup Pitch"
                subtitle="Winner (2024)"
              />
              <BackgroundItem
                title="University of Colorado Boulder"
                subtitle="Leeds School of Business"
              />
            </motion.div>

            {/* Featured Writing */}
            <motion.h2 variants={staggerItem} style={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#0f0f0f',
              marginBottom: '1.25rem',
            }}>
              Featured Writing
            </motion.h2>

            <motion.div variants={staggerItem} style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              marginBottom: '2.5rem',
            }}>
              <ArticleLink
                title="The Future of Deepfake Scams"
                href="/blog/deepfake-scams-future-threat"
              />
              <ArticleLink
                title="How AI Voice Cloning Works"
                href="/blog"
              />
            </motion.div>

            {/* CTA */}
            <motion.div variants={staggerItem} style={{
              background: 'rgba(99, 102, 241, 0.10)',
              border: '1px solid rgba(99, 102, 241, 0.2)',
              borderRadius: '16px',
              padding: '1.5rem',
              textAlign: 'center',
            }}>
              <p style={{
                color: '#3f3f3f',
                fontSize: '1rem',
                marginBottom: '1rem',
              }}>
                Want to follow along? Join The Human+AI Project.
              </p>
              <Link
                to="/human-ai-project"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: '#111111',
                  color: '#ffffff',
                  padding: '0.6rem 1.25rem',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  transition: 'transform 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                Subscribe
              </Link>
            </motion.div>
          </StaggerContainer>
        </div>
      </section>

    </div>
  )
}

function Tag({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.35rem',
      padding: '0.35rem 0.75rem',
      background: '#fafafa',
      border: '1px solid #eaeaea',
      borderRadius: '20px',
      color: '#5f5f5f',
      fontSize: '0.8rem',
    }}>
      {icon}
      {text}
    </span>
  )
}

function SocialIcon({ href, label, icon }: { href: string; label: string; icon: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      style={{
        width: '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fafafa',
        border: '1px solid #eaeaea',
        borderRadius: '10px',
        color: '#5f5f5f',
        transition: 'all 0.2s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.3)'
        e.currentTarget.style.color = '#a5b4fc'
        e.currentTarget.style.background = 'rgba(99, 102, 241, 0.1)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#dddddd'
        e.currentTarget.style.color = '#5f5f5f'
        e.currentTarget.style.background = '#f5f5f5'
      }}
    >
      {icon}
    </a>
  )
}

function WorkCard({ icon, title, description, link }: { icon: React.ReactNode; title: string; description: string; link: string }) {
  return (
    <Link to={link} style={{ textDecoration: 'none' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          padding: '1rem',
          background: '#fafafa',
          border: '1px solid #eaeaea',
          borderRadius: '12px',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.3)'
          e.currentTarget.style.transform = 'translateX(4px)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#eaeaea'
          e.currentTarget.style.transform = 'translateX(0)'
        }}
      >
        <div style={{
          width: '36px',
          height: '36px',
          background: 'rgba(99, 102, 241, 0.15)',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#4f46e5',
          flexShrink: 0,
        }}>
          {icon}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#0f0f0f', fontWeight: 500, fontSize: '0.95rem' }}>{title}</div>
          <div style={{ color: '#6b6b6b', fontSize: '0.85rem' }}>{description}</div>
        </div>
        <ExternalLink size={14} style={{ color: '#6b6b6b' }} />
      </div>
    </Link>
  )
}

function BackgroundItem({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      paddingBottom: '0.75rem',
      borderBottom: '1px solid #eaeaea',
    }}>
      <span style={{ color: '#0f0f0f', fontSize: '0.95rem' }}>{title}</span>
      <span style={{ color: '#6b6b6b', fontSize: '0.85rem' }}>{subtitle}</span>
    </div>
  )
}

function ArticleLink({ title, href }: { title: string; href: string }) {
  return (
    <Link
      to={href}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.75rem 1rem',
        background: '#fafafa',
        border: '1px solid #eaeaea',
        borderRadius: '8px',
        textDecoration: 'none',
        transition: 'all 0.2s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.3)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#eaeaea'
      }}
    >
      <span style={{ color: '#3f3f3f', fontSize: '0.9rem' }}>{title}</span>
      <ExternalLink size={14} style={{ color: '#6b6b6b' }} />
    </Link>
  )
}
